import React, { useState, useEffect } from 'react';

// Kalender-Komponente fuer Kennenlerntermin-Auswahl
function KennenlernKalender({ selectedTermine, onTerminAdd, onTerminRemove, maxTermine = 3 }) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    const monthNames = [
        'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];

    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

    const timeSlots = [];
    for (let hour = 10; hour < 20; hour++) {
        timeSlots.push({
            start: `${String(hour).padStart(2, '0')}:00`,
            end: `${String(hour + 1).padStart(2, '0')}:00`
        });
    }

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        let startDayOfWeek = firstDay.getDay() - 1;
        if (startDayOfWeek < 0) startDayOfWeek = 6;

        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateObj = new Date(year, month, day);
            const dayOfWeek = dateObj.getDay();
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
            const isFuture = dateObj > today;

            days.push({
                day,
                date: dateStr,
                dateObj,
                available: isWeekday && isFuture,
                dayOfWeek
            });
        }

        return days;
    };

    const getWeeks = (days) => {
        const weeks = [];
        let currentWeek = [];

        days.forEach((day) => {
            currentWeek.push(day);
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        });

        while (currentWeek.length > 0 && currentWeek.length < 7) {
            currentWeek.push(null);
        }
        if (currentWeek.length > 0) {
            weeks.push(currentWeek);
        }

        return weeks;
    };

    const goToPrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const handleDateClick = (dayObj) => {
        if (dayObj.available && selectedTermine.length < maxTermine) {
            setSelectedDate(dayObj);
        }
    };

    const handleTimeSelect = (slot) => {
        if (selectedTermine.length >= maxTermine) return;
        const terminValue = `${selectedDate.date}T${slot.start}`;
        if (!selectedTermine.includes(terminValue)) {
            onTerminAdd(terminValue);
        }
    };

    const formatTermin = (terminStr) => {
        if (!terminStr || !terminStr.includes('T')) return terminStr;
        const [dateStr, timeStr] = terminStr.split('T');
        const date = new Date(dateStr);
        const dateLabel = date.toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        const [hour] = timeStr.split(':').map(Number);
        return `${dateLabel}, ${timeStr} - ${String(hour + 1).padStart(2, '0')}:00 Uhr`;
    };

    const days = getDaysInMonth(currentMonth);
    const weeks = getWeeks(days);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const canGoPrev = prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1);

    return (
        <div>
            {/* Fortschrittsanzeige */}
            <div className={`alert ${selectedTermine.length === maxTermine ? 'alert-success' : 'alert-info'} mb-3`}>
                <div className="d-flex justify-content-between align-items-center">
                    <span>
                        {selectedTermine.length === 0 && <strong>Bitte waehle 3 Wunschtermine aus dem Kalender</strong>}
                        {selectedTermine.length === 1 && <strong>Bitte waehle noch 2 weitere Termine</strong>}
                        {selectedTermine.length === 2 && <strong>Bitte waehle noch 1 weiteren Termin</strong>}
                        {selectedTermine.length === maxTermine && <strong>Alle 3 Wunschtermine ausgewaehlt!</strong>}
                    </span>
                    <span className="badge bg-white text-dark fs-6">{selectedTermine.length} / {maxTermine}</span>
                </div>
            </div>

            {/* Ausgewaehlte Termine */}
            {selectedTermine.length > 0 && (
                <div className="mb-4">
                    <label className="form-label fw-bold small text-muted">Deine Wunschtermine:</label>
                    <div className="d-flex flex-column gap-2">
                        {selectedTermine.map((termin, idx) => (
                            <div key={termin} className="d-flex align-items-center justify-content-between bg-light rounded p-2 border">
                                <span>
                                    <span className="badge bg-info me-2">{idx + 1}</span>
                                    {formatTermin(termin)}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => onTerminRemove(termin)}
                                    title="Termin entfernen"
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Kalender - immer sichtbar */}
            <div className="card border-info mb-3">
                <div className="card-header bg-light">
                    {/* Kalender Navigation */}
                    <div className="d-flex justify-content-between align-items-center">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={goToPrevMonth}
                            disabled={!canGoPrev}
                        >
                            &larr;
                        </button>
                        <strong>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</strong>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goToNextMonth}>
                            &rarr;
                        </button>
                    </div>
                </div>
                <div className="card-body p-2">
                    {/* Kalender Tabelle */}
                    <table className="table table-bordered table-sm mb-0">
                        <thead>
                            <tr>
                                {dayNames.map((name, idx) => (
                                    <th key={idx} className="text-center p-1" style={{ width: '14.28%' }}>
                                        <small className="fw-bold">{name}</small>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {weeks.map((week, weekIdx) => (
                                <tr key={weekIdx}>
                                    {week.map((dayObj, dayIdx) => {
                                        const isSelectable = dayObj?.available && selectedTermine.length < maxTermine;
                                        // Prüfe ob an diesem Tag bereits Termine ausgewählt wurden
                                        const termineAnDiesemTag = dayObj ? selectedTermine.filter(t => t.startsWith(dayObj.date)).length : 0;
                                        const hasSelectedTermin = termineAnDiesemTag > 0;

                                        return (
                                            <td
                                                key={dayIdx}
                                                className={`text-center p-1 ${
                                                    dayObj?.date === todayStr ? 'bg-warning bg-opacity-10' : ''
                                                } ${
                                                    selectedDate?.date === dayObj?.date ? 'bg-info bg-opacity-25' : ''
                                                } ${
                                                    hasSelectedTermin ? 'bg-success bg-opacity-25' : ''
                                                }`}
                                                style={{
                                                    height: '50px',
                                                    cursor: isSelectable ? 'pointer' : 'default',
                                                    opacity: dayObj && !dayObj.available ? 0.3 : (dayObj?.available && !isSelectable ? 0.5 : 1),
                                                    border: hasSelectedTermin ? '2px solid #198754' : undefined
                                                }}
                                                onClick={() => isSelectable && handleDateClick(dayObj)}
                                            >
                                                {dayObj && (
                                                    <div>
                                                        <div className={`${isSelectable ? 'fw-bold text-info' : 'text-muted'} ${hasSelectedTermin ? 'text-success' : ''}`}>
                                                            {dayObj.day}
                                                        </div>
                                                        {hasSelectedTermin && (
                                                            <div>
                                                                <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>
                                                                    {termineAnDiesemTag}x
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Zeitauswahl - direkt im Kalender-Card */}
                {selectedDate && selectedTermine.length < maxTermine && (
                    <div className="card-footer bg-info bg-opacity-10">
                        <div className="mb-2">
                            <strong>
                                {selectedDate.dateObj.toLocaleDateString('de-DE', {
                                    weekday: 'long',
                                    day: '2-digit',
                                    month: 'long'
                                })}
                            </strong>
                            <span className="text-muted ms-2">- Waehle eine Uhrzeit:</span>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {timeSlots.map((slot, idx) => {
                                const terminValue = `${selectedDate.date}T${slot.start}`;
                                const isAlreadySelected = selectedTermine.includes(terminValue);
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        className={`btn btn-sm ${isAlreadySelected ? 'btn-secondary' : 'btn-outline-info'}`}
                                        onClick={() => !isAlreadySelected && handleTimeSelect(slot)}
                                        disabled={isAlreadySelected}
                                    >
                                        {slot.start}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Legende */}
            <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
                <div className="d-flex align-items-center">
                    <span className="d-inline-block me-1 fw-bold text-info">12</span> Verfuegbar
                </div>
                <div className="d-flex align-items-center">
                    <span className="d-inline-block me-1 px-1 rounded bg-success bg-opacity-25 text-success border border-success" style={{ fontSize: '0.75rem' }}>12</span> Bereits gewaehlt
                </div>
                <div className="d-flex align-items-center">
                    <span className="d-inline-block me-1 px-1 rounded bg-info bg-opacity-25 text-info" style={{ fontSize: '0.75rem' }}>12</span> Aktuell ausgewaehlt
                </div>
            </div>

            {/* Hinweis wenn kein Datum ausgewaehlt */}
            {!selectedDate && selectedTermine.length < maxTermine && (
                <p className="text-muted small mb-0">
                    Klicke auf einen verfuegbaren Tag (Mo-Fr) im Kalender, um eine Uhrzeit auszuwaehlen.
                </p>
            )}
        </div>
    );
}

function Schnuppern() {
    const [activeTab, setActiveTab] = useState('probewoche');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    // Kennenlerntermine State
    const [selectedKennenlernTermine, setSelectedKennenlernTermine] = useState([]);

    // Schnuppertermine State (feste Gruppentermine, 1 Stunde, 5 Euro)
    const [schnupperSlots, setSchnupperSlots] = useState([]);
    const [loadingSchnupperSlots, setLoadingSchnupperSlots] = useState(true);
    const [selectedSchnupperSlot, setSelectedSchnupperSlot] = useState(null);

    useEffect(() => {
        fetch('/api/public/schnuppertermin-slots')
            .then(res => res.json())
            .then(data => {
                setSchnupperSlots(data.data || []);
                setLoadingSchnupperSlots(false);
            })
            .catch(() => setLoadingSchnupperSlots(false));
    }, []);

    // Formular State
    const [formData, setFormData] = useState({
        parent_name: '',
        email: '',
        phone: '',
        child_name: '',
        child_age: '',
        message: '',
        start_date: '',
        wunschtermin1: '',
        wunschtermin2: '',
        wunschtermin3: ''
    });

    const resetForm = () => {
        setFormData({
            parent_name: '',
            email: '',
            phone: '',
            child_name: '',
            child_age: '',
            message: '',
            start_date: '',
            wunschtermin1: '',
            wunschtermin2: '',
            wunschtermin3: ''
        });
        setSelectedKennenlernTermine([]);
    };

    const handleProbewocheSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        fetch('/api/public/probewoche-buchen', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...formData,
                child_age: formData.child_age ? parseInt(formData.child_age) : null
            })
        })
            .then(res => res.json())
            .then(data => {
                setSubmitting(false);
                if (data.message === 'success') {
                    setSuccess({
                        type: 'probewoche',
                        ...data.data,
                        parent_name: formData.parent_name
                    });
                    resetForm();
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(err => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    const handleKennenlernSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        fetch('/api/public/kennenlernen-buchen', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                parent_name: formData.parent_name,
                email: formData.email,
                phone: formData.phone,
                child_name: formData.child_name,
                child_age: formData.child_age ? parseInt(formData.child_age) : null,
                message: formData.message,
                wunschtermin1: selectedKennenlernTermine[0] || '',
                wunschtermin2: selectedKennenlernTermine[1] || '',
                wunschtermin3: selectedKennenlernTermine[2] || ''
            })
        })
            .then(res => res.json())
            .then(data => {
                setSubmitting(false);
                if (data.message === 'success') {
                    setSuccess({
                        type: 'kennenlernen',
                        ...data.data,
                        parent_name: formData.parent_name
                    });
                    resetForm();
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(err => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    const handleSchnupperterminSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        fetch('/api/public/schnuppertermin-buchen', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                slot_id: selectedSchnupperSlot.id,
                parent_name: formData.parent_name,
                email: formData.email,
                phone: formData.phone,
                child_name: formData.child_name,
                child_age: formData.child_age ? parseInt(formData.child_age) : null,
                message: formData.message
            })
        })
            .then(res => res.json())
            .then(data => {
                setSubmitting(false);
                if (data.message === 'success') {
                    setSuccess({
                        type: 'schnuppertermin',
                        ...data.data,
                        parent_name: formData.parent_name
                    });
                    setSelectedSchnupperSlot(null);
                    resetForm();
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(() => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    // Formatiert Kennenlerntermin (Format: YYYY-MM-DDTHH:MM)
    const formatKennenlernTermin = (terminStr) => {
        if (!terminStr || !terminStr.includes('T')) return terminStr;
        const [dateStr, timeStr] = terminStr.split('T');
        const date = new Date(dateStr);
        const dateLabel = date.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
        const [hour] = timeStr.split(':').map(Number);
        return `${dateLabel}, ${timeStr} - ${String(hour + 1).padStart(2, '0')}:00 Uhr`;
    };

    // Naechsten Montag berechnen
    const getNextMonday = () => {
        const today = new Date();
        const day = today.getDay();
        const daysUntilMonday = day === 0 ? 1 : 8 - day;
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + daysUntilMonday);
        return nextMonday.toISOString().split('T')[0];
    };

    // Montage fuer die naechsten 8 Wochen generieren
    const getAvailableMondays = () => {
        const mondays = [];
        let monday = new Date(getNextMonday());
        for (let i = 0; i < 8; i++) {
            mondays.push({
                value: monday.toISOString().split('T')[0],
                label: monday.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
            });
            monday.setDate(monday.getDate() + 7);
        }
        return mondays;
    };

    // Success View
    if (success) {
        return (
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="card border-success">
                            <div className="card-body text-center py-5">
                                <div className="mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                </div>
                                <h2 className="text-success mb-3">
                                    {success.type === 'probewoche' && 'Schnupper-Pass gebucht!'}
                                    {success.type === 'kennenlernen' && 'Danke fuer deine Anfrage!'}
                                    {success.type === 'schnuppertermin' && 'Schnuppertermin gebucht!'}
                                </h2>
                                <p className="lead mb-4">
                                    Vielen Dank, {success.parent_name}!<br />
                                    {success.type === 'probewoche' && 'Dein Schnupper-Pass wurde vorgemerkt.'}
                                    {success.type === 'kennenlernen' && 'Wir melden uns zeitnah bei dir, um einen Termin zu bestaetigen.'}
                                    {success.type === 'schnuppertermin' && 'Wir freuen uns auf dich!'}
                                </p>
                                <div className="bg-light rounded p-3 mb-4">
                                    {success.type === 'probewoche' && (
                                        <>
                                            <strong>Start: {formatDate(success.start_date)}</strong><br />
                                            <span className="text-primary fw-bold">49,00 Euro</span><br />
                                            <small className="text-muted">4 Wochen · 2 Kurse pro Woche</small>
                                        </>
                                    )}
                                    {success.type === 'kennenlernen' && (
                                        <>
                                            <strong>Deine Wunschtermine:</strong><br />
                                            1. {formatKennenlernTermin(success.wunschtermin1)}<br />
                                            2. {formatKennenlernTermin(success.wunschtermin2)}<br />
                                            3. {formatKennenlernTermin(success.wunschtermin3)}
                                        </>
                                    )}
                                    {success.type === 'schnuppertermin' && (
                                        <>
                                            <strong>{formatDate(success.date)}</strong><br />
                                            <span className="text-muted">{success.time?.substring(0, 5)} Uhr · {success.duration} Minuten</span><br />
                                            <span className="text-primary fw-bold">{parseFloat(success.price).toFixed(2)} Euro</span>
                                        </>
                                    )}
                                </div>
                                <p className="text-muted mb-4">
                                    Wir melden uns zeitnah bei dir mit allen weiteren Informationen.
                                    {success.type === 'kennenlernen' && (
                                        <>
                                            <br /><br />
                                            <small>Du erhaeltst eine Bestaetigungs-Email. Bitte schaue auch in deinem Spam-Ordner nach, falls du keine Email erhaeltst.</small>
                                        </>
                                    )}
                                    {success.type === 'schnuppertermin' && (
                                        <>
                                            <br /><br />
                                            <small>Der Betrag wird bei einer anschließenden Mitgliedschaft auf den ersten Monatsbeitrag angerechnet. Du erhaeltst eine Bestaetigungs-Email.</small>
                                        </>
                                    )}
                                </p>
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => setSuccess(null)}
                                >
                                    Weitere Buchung
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <div className="text-center mb-4">
                        <h1 className="h2">Schnuppern</h1>
                        <p className="lead text-muted">
                            Lerne uns kennen - waehle deinen Einstieg!
                        </p>
                    </div>

                    {/* Tab Navigation */}
                    <ul className="nav nav-pills nav-justified mb-4">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'probewoche' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('probewoche'); setError(null); }}
                            >
                                <div className="fw-bold">Schnupper-Pass</div>
                                <small>49 Euro / Monat</small>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'kennenlernen' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('kennenlernen'); setError(null); }}
                            >
                                <div className="fw-bold">Kennenlerntermin</div>
                                <small>Einrichtung besichtigen</small>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'schnuppertermin' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('schnuppertermin'); setError(null); }}
                            >
                                <div className="fw-bold">Schnuppertermin</div>
                                <small>5 Euro / 1 Stunde</small>
                            </button>
                        </li>
                    </ul>

                    {/* Tab Content */}
                    {activeTab === 'probewoche' && (
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card border-primary mb-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h4 className="card-title mb-0">Schnupper-Pass</h4>
                                            <span className="badge bg-primary fs-5">49 Euro / Monat</span>
                                        </div>
                                        <p className="card-text">
                                            Einen Monat lang ganz unverbindlich alle Kurse testen — perfekt um herauszufinden, was passt.
                                        </p>
                                        <ul className="list-unstyled">
                                            <li className="mb-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-success me-2" viewBox="0 0 16 16">
                                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                                </svg>
                                                <strong>2× pro Woche</strong> — Kurse frei wählbar
                                            </li>
                                            <li className="mb-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-success me-2" viewBox="0 0 16 16">
                                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                                </svg>
                                                <strong>1× gratis schnuppern</strong> — vorab unverbindlich
                                            </li>
                                            <li className="mb-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-success me-2" viewBox="0 0 16 16">
                                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                                </svg>
                                                <strong>Keine Mindestlaufzeit</strong> — monatlich kündbar
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-header bg-primary text-white">
                                        <h5 className="mb-0">Schnupper-Pass buchen</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleProbewocheSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">Dein Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.parent_name}
                                                    onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                                    required
                                                    placeholder="Vor- und Nachname"
                                                />
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">E-Mail *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={formData.email}
                                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                        required
                                                        placeholder="deine@email.de"
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Telefon</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        value={formData.phone}
                                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                        placeholder="Optional"
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-4" />
                                            <h6 className="mb-3">Kind</h6>

                                            <div className="row">
                                                <div className="col-md-8 mb-3">
                                                    <label className="form-label">Name des Kindes *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.child_name}
                                                        onChange={e => setFormData({ ...formData, child_name: e.target.value })}
                                                        required
                                                        placeholder="Vorname"
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Alter</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.child_age}
                                                        onChange={e => setFormData({ ...formData, child_age: e.target.value })}
                                                        min="6"
                                                        max="18"
                                                        placeholder="Jahre"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Startdatum (Montag) *</label>
                                                <select
                                                    className="form-select"
                                                    value={formData.start_date}
                                                    onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                                                    required
                                                >
                                                    <option value="">Bitte waehlen...</option>
                                                    {getAvailableMondays().map(m => (
                                                        <option key={m.value} value={m.value}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label">Nachricht / Fragen</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={formData.message}
                                                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                                                    placeholder="Gibt es etwas, das wir wissen sollten?"
                                                />
                                            </div>

                                            {error && (
                                                <div className="alert alert-danger">{error}</div>
                                            )}

                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-lg w-100"
                                                disabled={submitting}
                                            >
                                                {submitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Wird gebucht...
                                                    </>
                                                ) : 'Schnupper-Pass buchen (49 Euro / Monat)'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'kennenlernen' && (
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card border-info mb-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h4 className="card-title mb-0">Kennenlerntermin</h4>
                                            <span className="badge bg-info fs-5">Kostenlos</span>
                                        </div>
                                        <p className="card-text text-muted">
                                            Besuche uns und lerne unsere Einrichtung kennen. Wir zeigen dir alles und
                                            beantworten deine Fragen zum Konzept.
                                        </p>
                                        <p className="card-text">
                                            <strong>Termine:</strong> Montag - Freitag, 10:00 - 20:00 Uhr (max. 1 Stunde)
                                        </p>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0">Kennenlerntermin anfragen</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleKennenlernSubmit}>
                                            {/* Wunschtermine per Kalender */}
                                            <KennenlernKalender
                                                selectedTermine={selectedKennenlernTermine}
                                                onTerminAdd={(termin) => setSelectedKennenlernTermine([...selectedKennenlernTermine, termin])}
                                                onTerminRemove={(termin) => setSelectedKennenlernTermine(selectedKennenlernTermine.filter(t => t !== termin))}
                                                maxTermine={3}
                                            />

                                            <hr className="my-4" />

                                            {/* Kontaktdaten */}
                                            <div className="mb-3">
                                                <label className="form-label">Dein Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.parent_name}
                                                    onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                                    required
                                                    placeholder="Vor- und Nachname"
                                                />
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">E-Mail *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={formData.email}
                                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                        required
                                                        placeholder="deine@email.de"
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Telefon</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        value={formData.phone}
                                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                        placeholder="Optional"
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-4" />
                                            <h6 className="mb-3">Kind (optional)</h6>

                                            <div className="row">
                                                <div className="col-md-8 mb-3">
                                                    <label className="form-label">Name des Kindes</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.child_name}
                                                        onChange={e => setFormData({ ...formData, child_name: e.target.value })}
                                                        placeholder="Vorname"
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Alter</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.child_age}
                                                        onChange={e => setFormData({ ...formData, child_age: e.target.value })}
                                                        min="6"
                                                        max="18"
                                                        placeholder="Jahre"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label">Nachricht / Fragen</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={formData.message}
                                                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                                                    placeholder="Gibt es etwas, das wir wissen sollten?"
                                                />
                                            </div>

                                            {error && (
                                                <div className="alert alert-danger">{error}</div>
                                            )}

                                            <button
                                                type="submit"
                                                className="btn btn-info btn-lg w-100 text-white"
                                                disabled={submitting || selectedKennenlernTermine.length < 3}
                                            >
                                                {submitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Wird gesendet...
                                                    </>
                                                ) : 'Kennenlerntermin anfragen'}
                                            </button>

                                            <p className="text-muted small text-center mt-3 mb-0">
                                                Wir melden uns bei dir, um einen der Termine zu bestaetigen.
                                            </p>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'schnuppertermin' && (
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card border-success mb-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h4 className="card-title mb-0">Schnuppertermin</h4>
                                            <span className="badge bg-success fs-5">5 Euro</span>
                                        </div>
                                        <p className="card-text text-muted">
                                            Eine Stunde live dabei sein und unsere Kurse ausprobieren — in kleiner Gruppe,
                                            ganz unverbindlich. Wir bieten dafuer jeden Monat zwei feste Termine an.
                                        </p>
                                        <p className="card-text mb-0">
                                            <small className="text-success">
                                                Die 5 Euro werden bei einer anschließenden Mitgliedschaft auf den ersten Monatsbeitrag angerechnet.
                                            </small>
                                        </p>
                                    </div>
                                </div>

                                {!selectedSchnupperSlot ? (
                                    loadingSchnupperSlots ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-success" role="status">
                                                <span className="visually-hidden">Laden...</span>
                                            </div>
                                        </div>
                                    ) : schnupperSlots.length === 0 ? (
                                        <div className="alert alert-info">
                                            Aktuell sind keine Schnuppertermine geplant. Schau bald wieder vorbei oder
                                            kontaktiere uns direkt fuer den naechsten Termin.
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {schnupperSlots.map(slot => {
                                                const spotsLeft = slot.max_participants - parseInt(slot.booked_count);
                                                const isFull = spotsLeft <= 0;
                                                return (
                                                    <div key={slot.id} className="col-md-6">
                                                        <div
                                                            className={`card h-100 ${isFull ? 'border-secondary' : 'border-success'}`}
                                                            style={{ cursor: isFull ? 'default' : 'pointer', opacity: isFull ? 0.7 : 1 }}
                                                            onClick={() => !isFull && setSelectedSchnupperSlot(slot)}
                                                        >
                                                            <div className="card-body">
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <h6 className="card-title mb-0">{formatDate(slot.date)}</h6>
                                                                    {isFull ? (
                                                                        <span className="badge bg-secondary">Ausgebucht</span>
                                                                    ) : (
                                                                        <span className="badge bg-success">{spotsLeft} {spotsLeft === 1 ? 'Platz' : 'Plaetze'}</span>
                                                                    )}
                                                                </div>
                                                                <p className="card-text mb-0">
                                                                    {slot.time?.substring(0, 5)} Uhr · {slot.duration} Minuten
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                ) : (
                                    <div className="card">
                                        <div className="card-header bg-success text-white">
                                            <h5 className="mb-0">
                                                Schnuppertermin am {formatDate(selectedSchnupperSlot.date)}, {selectedSchnupperSlot.time?.substring(0, 5)} Uhr
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <form onSubmit={handleSchnupperterminSubmit}>
                                                <div className="mb-3">
                                                    <label className="form-label">Dein Name *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.parent_name}
                                                        onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                                        required
                                                        placeholder="Vor- und Nachname"
                                                    />
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">E-Mail *</label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            value={formData.email}
                                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                            required
                                                            placeholder="deine@email.de"
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Telefon</label>
                                                        <input
                                                            type="tel"
                                                            className="form-control"
                                                            value={formData.phone}
                                                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                            placeholder="Optional"
                                                        />
                                                    </div>
                                                </div>

                                                <hr className="my-4" />
                                                <h6 className="mb-3">Kind</h6>

                                                <div className="row">
                                                    <div className="col-md-8 mb-3">
                                                        <label className="form-label">Name des Kindes *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.child_name}
                                                            onChange={e => setFormData({ ...formData, child_name: e.target.value })}
                                                            required
                                                            placeholder="Vorname"
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Alter</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={formData.child_age}
                                                            onChange={e => setFormData({ ...formData, child_age: e.target.value })}
                                                            min="6"
                                                            max="18"
                                                            placeholder="Jahre"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <label className="form-label">Nachricht / Fragen</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="3"
                                                        value={formData.message}
                                                        onChange={e => setFormData({ ...formData, message: e.target.value })}
                                                        placeholder="Gibt es etwas, das wir wissen sollten?"
                                                    />
                                                </div>

                                                {error && (
                                                    <div className="alert alert-danger">{error}</div>
                                                )}

                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-success btn-lg flex-grow-1"
                                                        disabled={submitting}
                                                    >
                                                        {submitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Wird gebucht...
                                                            </>
                                                        ) : 'Schnuppertermin buchen (5 Euro)'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-lg"
                                                        onClick={() => setSelectedSchnupperSlot(null)}
                                                        disabled={submitting}
                                                    >
                                                        Zurueck
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Schnuppern;
